import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import Footer, { startLogin } from "@/components/Footer";
import LandingChat from "@/components/LandingChat";
import OurPromise from "@/components/OurPromise";
import PlansPreview from "@/components/PlansPreview";
import { STORIES } from "@/pages/Blog";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight, ShieldCheck, Cpu, UserCheck, FileLock2, Home as HomeIcon,
  FilePlus2, UploadCloud, Sparkles, TrendingDown,
  AlertTriangle, Quote, Star, IndianRupee, X, Boxes, FileText,
  CircleDollarSign, MessageSquare, Bot, BadgeCheck
} from "lucide-react";

const trustBadges = [
  { icon: Cpu, label: "AI-Powered Construction Intelligence" },
  { icon: UserCheck, label: "Independent Expert Advisors" },
  { icon: FileLock2, label: "Secure Document Analysis" },
  { icon: HomeIcon, label: "Built for Independent Homeowners" },
];

// What can Nirmantra help me with — the core homeowner problems
const helpItems = [
  { icon: IndianRupee, title: "Know what your construction should cost", desc: "Get a clear, India-specific cost benchmark before you commit." },
  { icon: AlertTriangle, title: "Detect risks and missing items", desc: "Catch structural, budget and contract red flags early." },
  { icon: Boxes, title: "Understand and compare materials", desc: "Choose the right materials with objective, independent guidance." },
  { icon: FileText, title: "Review BOQs and quotations", desc: "See what's fair, inflated or missing in every quote." },
  { icon: ShieldCheck, title: "Find verified construction partners", desc: "Discover professionals matched to your project requirements." },
  { icon: UserCheck, title: "Get independent expert guidance", desc: "Talk to an advisor who works for you — never a contractor." },
];

// NCIS example questions (map to what the AI can answer)
const ncisQuestions = [
  "What should my construction cost?",
  "Is this quotation reasonable?",
  "Which material should I choose?",
  "Where can I reduce unnecessary cost?",
  "What risks should I check before starting?",
];

const steps = [
  { n: "01", icon: FilePlus2, title: "Create Your Project", desc: "Add your plot size, budget, city and construction type in under a minute." },
  { n: "02", icon: UploadCloud, title: "Upload Drawings / BOQ / Quotations", desc: "Securely upload floor plans, BOQs, quotations, soil reports and site images." },
  { n: "03", icon: Cpu, title: "NCIS Analyses Your Information", desc: "NCIS™ reviews your inputs against cost logic, material and construction intelligence." },
  { n: "04", icon: Sparkles, title: "Receive Your Construction Health Report", desc: "Get an instant report with score, cost, risks and savings opportunities." },
];

// Three core differentiators
const differentiators = [
  { icon: Cpu, title: "Independent Construction Intelligence", desc: "An advisory platform built for property owners — we never take construction work, so our guidance is always in your interest." },
  { icon: Bot, title: "NCIS-Powered Decision Support", desc: "NCIS™ combines construction knowledge, cost logic and material intelligence to help you decide with clarity, not guesswork." },
  { icon: CircleDollarSign, title: "Zero Commission Advisory", desc: "We never take a commission on the projects you execute. Every recommendation is driven by your requirements — not vendor payments." },
];

const faqs = [
  ["Is Nirmantra a contractor or builder?", "No. Nirmantra.ai is an independent construction intelligence and advisory platform for homeowners. We do not build or execute construction, so our guidance stays fully in your interest."],
  ["Does Nirmantra take commission from construction projects?", "No. We never charge a commission on construction projects you execute — regardless of your build size or which partner you choose."],
  ["How does Nirmantra make money?", "Through optional advisory plans and expert consultations. Our core AI Construction Health Report is free and self-serve — we earn when we help you decide better, never from your project cost."],
  ["What is NCIS™?", "NCIS™ is Nirmantra's construction intelligence system that combines construction knowledge, project data, cost logic, material intelligence and AI to help homeowners make better construction decisions."],
  ["Can NCIS replace an architect or engineer?", "No. NCIS™ supports your decisions with intelligence and a second opinion. It complements — not replaces — your architect, engineer or contractor."],
  ["How accurate is the construction estimate?", "Estimates use India-specific market rates based on your plot size, floors, city and uploaded documents. They're a strong planning benchmark — the more documents you add, the sharper the estimate."],
  ["What documents can I upload?", "Floor plans, BOQs (Bill of Quantities), contractor quotations, soil reports and site images, in PDF, image, Excel or CSV formats."],
  ["Are vendors verified?", "Yes. Only partners who complete Nirmantra's verification and go live appear in our network as 'Nirmantra Verified Partner'. A merely discovered business is not shown as verified."],
  ["How does vendor verification work?", "Vendors register, are reviewed by our team, verified, approved and only then made live. You always choose your own partner — there is no lock-in."],
  ["Is my project data secure?", "Yes. Your documents are stored securely, are only accessible to you, and we never share your project data."],
  ["Do I need an expert consultation?", "Not at all. The AI Construction Health Report is fully self-serve. Expert consultations are an optional escalation layer for when you want deeper human guidance."],
  ["Who is Nirmantra designed for?", "Independent homeowners and plot owners building their own homes who want clarity, cost confidence and protection before making expensive construction decisions."],
];

export default function Landing() {
  const [showPromo, setShowPromo] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const askNcis = () => {
    const el = document.getElementById("ncis-chat");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => document.querySelector('[data-testid="landing-chat-input"]')?.focus({ preventScroll: true }), 700);
  };
  const createProject = () => (user ? navigate("/new-project") : startLogin("/new-project"));

  useEffect(() => {
    api.get("/testimonials").then((res) => setTestimonials(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {showPromo && (
        <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-[#0B7EA4] to-slate-900" data-testid="promo-banner">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-center gap-3">
            <span className="hidden sm:inline-flex items-center rounded-full bg-[#F5923C] text-slate-900 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider">Free</span>
            <p className="text-white text-xs sm:text-sm text-center">
              <Sparkles className="inline h-3.5 w-3.5 mb-0.5 text-[#F5923C]" strokeWidth={2} />{" "}
              <span className="font-bold">Get your free Construction Health Report</span> — understand your cost, risks & quotations before you spend.
            </p>
            <button onClick={startLogin} data-testid="promo-cta"
              className="hidden md:inline-flex items-center gap-1 rounded-full bg-white text-slate-900 hover:bg-[#F5923C] font-semibold text-xs px-3 py-1 transition-colors">
              Get Free Report <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
            </button>
            <button onClick={() => setShowPromo(false)} data-testid="promo-dismiss" aria-label="Dismiss"
              className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors">
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
      <PublicNav />

      {/* ============ HERO — highest priority ============ */}
      <section className="hero-wash relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 pt-16 pb-20 lg:pt-24 lg:pb-28 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 mb-6 shadow-sm">
              <ShieldCheck className="h-4 w-4 text-[#0E9AC8]" strokeWidth={2} />
              <span className="text-xs font-semibold tracking-wide text-slate-600">Independent Construction Intelligence for Homeowners</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.04] tracking-tighter font-extrabold text-slate-900">
              Build Your Dream Home<br /><span className="text-[#0E9AC8]">with Confidence.</span>
            </h1>
            <p className="mt-4 text-sm font-semibold tracking-wide text-slate-500 uppercase">
              India's AI-Powered Construction Advisor for Independent Homeowners
            </p>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">
              Tell Nirmantra about your project and get construction intelligence — costs, materials, quotations
              and risks — tailored to your requirements, before you spend.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button onClick={createProject} data-testid="hero-primary-cta"
                className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-14 px-8 text-base font-semibold shadow-lg shadow-[#0E9AC8]/20 transition-colors">
                Create My Project <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
              </Button>
              <Button onClick={askNcis} variant="outline" data-testid="hero-secondary-cta"
                className="rounded-xl h-14 px-7 text-base border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8] transition-colors">
                <MessageSquare className="mr-2 h-4 w-4" strokeWidth={2} /> Ask NCIS™
              </Button>
            </div>
            <div className="mt-4">
              <Link to="/partners" data-testid="hero-vendors-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0E9AC8] transition-colors">
                Or explore verified vendors <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3 max-w-lg">
              {trustBadges.map((b) => (
                <div key={b.label} className="flex items-center gap-2.5">
                  <b.icon className="h-4 w-4 text-[#0E9AC8] shrink-0" strokeWidth={1.75} />
                  <span className="text-xs sm:text-sm text-slate-600">{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 animate-fade-up" style={{ animationDelay: "0.12s" }}>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden card-premium">
                <img src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob21lJTIwY29uc3RydWN0aW9uJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NTczODIyNXww&ixlib=rb-4.1.0&q=85"
                  alt="Modern home" className="w-full h-[360px] lg:h-[460px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:left-6 card-premium rounded-2xl p-5 w-[230px]">
                <p className="text-[11px] uppercase tracking-wider text-slate-400">Health Score</p>
                <div className="flex items-end gap-1 mt-1">
                  <span className="font-display text-4xl font-extrabold text-slate-900">82</span>
                  <span className="text-slate-300 font-display font-bold mb-1">/100</span>
                </div>
                <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0E9AC8] rounded-full" style={{ width: "82%" }} />
                </div>
              </div>
              <div className="absolute -top-4 -right-2 sm:right-6 card-premium rounded-2xl p-4">
                <p className="text-[11px] uppercase tracking-wider text-slate-400">Potential Savings</p>
                <p className="font-display text-2xl font-extrabold text-[#0E9AC8] flex items-center"><IndianRupee className="h-4 w-4" />4.2L</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT CAN NIRMANTRA HELP ME WITH ============ */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20" data-testid="help-section">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">What can Nirmantra help me with?</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900">
            Clarity on every big construction decision.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {helpItems.map((h, i) => (
            <div key={h.title} data-testid={`help-card-${i}`} className="card-premium rounded-2xl p-6 flex gap-4">
              <div className="h-11 w-11 shrink-0 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center">
                <h.icon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.6} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 leading-snug">{h.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{h.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ POWERED BY NCIS ============ */}
      <div id="ncis" className="scroll-mt-20">
        <section className="relative overflow-hidden bg-[#0B1B2B]" data-testid="ncis-intro">
          <div className="absolute inset-0 ncis-grid-bg opacity-40" />
          <div className="absolute -top-16 left-1/3 h-64 w-64 rounded-full bg-[#0E9AC8]/25 blur-[120px]" />
          <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-20 text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-[#4FD1DB]" strokeWidth={2} />
              <span className="text-xs font-semibold tracking-wide text-white/80">Powered by NCIS™</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold text-white">
              The intelligence engine behind Nirmantra.
            </h2>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-white/70 max-w-2xl mx-auto">
              NCIS™ is Nirmantra's construction intelligence system that combines construction knowledge, project
              data, cost logic, material intelligence and AI to help homeowners make better construction decisions.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-2.5" data-testid="ncis-questions">
              {ncisQuestions.map((q) => (
                <span key={q} className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3.5 py-2 text-sm text-white/85">
                  <MessageSquare className="h-3.5 w-3.5 text-[#4FD1DB]" strokeWidth={1.75} /> {q}
                </span>
              ))}
            </div>
          </div>
        </section>
        {/* Live NCIS advisor chat */}
        <div id="ncis-chat" className="scroll-mt-20">
          <LandingChat />
        </div>
      </div>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how" className="scroll-mt-20 max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">How Nirmantra works</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900">
            From plans to peace of mind in four steps.
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s) => (
            <div key={s.n} data-testid={`step-${s.n}`}
              className="card-premium rounded-2xl p-7 hover:-translate-y-1.5 transition-transform duration-300">
              <div className="flex items-center justify-between">
                <div className="h-12 w-12 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center">
                  <s.icon className="h-6 w-6 text-[#0E9AC8]" strokeWidth={1.5} />
                </div>
                <span className="font-display text-4xl font-extrabold text-slate-100">{s.n}</span>
              </div>
              <h3 className="mt-6 font-display text-lg font-bold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <Button onClick={createProject} data-testid="how-cta"
            className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-14 px-8 text-base font-semibold shadow-lg shadow-[#0E9AC8]/20 transition-colors">
            Create My Project <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
          </Button>
        </div>
      </section>

      {/* ============ ZERO COMMISSION — major trust pillar ============ */}
      <OurPromise />

      {/* ============ WHY NIRMANTRA — three core differentiators ============ */}
      <section id="why" className="relative">
        <div className="absolute inset-0 soft-grid opacity-70" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">Why Nirmantra?</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900">
              Advice that's always on your side.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {differentiators.map((d, i) => (
              <div key={d.title} data-testid={`diff-card-${i}`}
                className="card-premium rounded-2xl p-8 hover:-translate-y-1.5 transition-transform duration-300">
                <div className="h-12 w-12 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center">
                  <d.icon className="h-6 w-6 text-[#0E9AC8]" strokeWidth={1.6} />
                </div>
                <h3 className="mt-6 font-display text-xl font-extrabold text-slate-900">{d.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ VERIFIED PARTNERS ============ */}
      <section className="max-w-7xl mx-auto px-6 pt-4 lg:pt-6">
        <div className="max-w-2xl mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">Need a construction partner?</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter font-extrabold text-slate-900">
            Discover verified professionals for your project.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Nirmantra helps you discover verified construction professionals based on your project requirements.
            We stay independent — the choice is always yours.
          </p>
        </div>
        <Link to="/partners" data-testid="partners-band" className="group block rounded-2xl bg-slate-900 overflow-hidden hover:shadow-lg transition-shadow">
          <div className="px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#0E9AC8] flex items-center justify-center shrink-0">
                <ShieldCheck className="h-6 w-6 text-white" strokeWidth={1.75} />
              </div>
              <div>
                <p className="font-display text-xl font-extrabold text-white">Nirmantra Verified Partners</p>
                <p className="text-sm text-white/70">Background-checked contractors, architects & suppliers across India.</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-900 group-hover:bg-[#F5923C] font-semibold text-sm px-5 py-2.5 transition-colors">
              Explore Verified Partners <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </span>
          </div>
        </Link>
      </section>

      {/* ============ PROOF — savings stories & testimonials ============ */}
      <section className="max-w-7xl mx-auto px-6 pt-16 lg:pt-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">Proof · Savings stories</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900">
              Homeowners building smarter.
            </h2>
          </div>
          <Link to="/blog" data-testid="stories-viewall" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E9AC8] hover:gap-2.5 transition-all">
            Read all stories <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STORIES.slice(0, 3).map((s) => (
            <Link key={s.id} to="/blog" data-testid={`story-card-${s.id}`}
              className="group card-premium rounded-2xl overflow-hidden hover:-translate-y-1.5 transition-transform duration-300">
              <div className="relative h-44 overflow-hidden">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 text-slate-900 px-2.5 py-1 text-xs font-bold shadow-sm">
                  <TrendingDown className="h-3.5 w-3.5 text-[#0E9AC8]" strokeWidth={2} /> Saved {s.saving}
                </span>
              </div>
              <div className="p-5">
                <p className="text-xs text-slate-400">{s.city} · {s.area}</p>
                <h3 className="mt-2 font-display text-base font-bold text-slate-900 leading-snug line-clamp-2">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{s.summary}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0E9AC8] group-hover:gap-2 transition-all">Read story <ArrowRight className="h-4 w-4" strokeWidth={2} /></span>
              </div>
            </Link>
          ))}
          {/* Fourth card — the 10 ways guide */}
          <Link to="/guide/save-20-percent" data-testid="story-card-guide"
            className="group card-premium rounded-2xl overflow-hidden hover:-translate-y-1.5 transition-transform duration-300">
            <div className="relative h-44 overflow-hidden">
              <img src="https://images.pexels.com/photos/19995510/pexels-photo-19995510.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Delhi NCR home construction" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#0E9AC8] text-white px-2.5 py-1 text-xs font-bold shadow-sm">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> Guide
              </span>
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-400">Delhi NCR · Cost saving guide</p>
              <h3 className="mt-2 font-display text-base font-bold text-slate-900 leading-snug line-clamp-2">10 ways to save up to 20% on your NCR home</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">From comparing quotations to design optimisation and material selection — the decisions that quietly cut your project cost.</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0E9AC8] group-hover:gap-2 transition-all">Read guide <ArrowRight className="h-4 w-4" strokeWidth={2} /></span>
            </div>
          </Link>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {testimonials.map((t) => (
            <div key={t.id} data-testid={`testimonial-${t.id}`} className="card-premium rounded-2xl p-7 flex flex-col">
              <Quote className="h-6 w-6 text-[#0E9AC8]/25" strokeWidth={1.5} />
              <p className="mt-4 text-slate-700 leading-relaxed flex-1">{t.review}</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold text-sm shrink-0">{t.initials}</div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 text-sm">{t.customer_name}</p>
                    {t.verified && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#0E9AC8]/10 text-[#0E9AC8] px-2 py-0.5 text-[11px] font-semibold" data-testid="verified-badge">
                        <BadgeCheck className="h-3.5 w-3.5" strokeWidth={2} /> Verified Homeowner
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t.customer_type} · {t.locality ? `${t.locality}, ` : ""}{t.city}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-400 text-center" data-testid="proof-disclaimer">
          Illustrative homeowner scenarios. Individual experiences and results may vary.
        </p>
      </section>

      {/* ============ ADVISORY PLANS — human expertise (escalation) ============ */}
      <PlansPreview />

      {/* ============ FAQ ============ */}
      <section id="faq" className="scroll-mt-20 max-w-3xl mx-auto px-6 py-20 lg:py-24">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">FAQ</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter font-extrabold text-slate-900">
            Questions, answered.
          </h2>
        </div>
        <Accordion type="single" collapsible className="mt-10">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`} className="border-slate-200">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline hover:text-[#0E9AC8]">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section id="cta" className="max-w-7xl mx-auto px-6 pb-20 lg:pb-28">
        <div className="relative rounded-3xl overflow-hidden">
          <img src="https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbW9kZXJuJTIwaG91c2UlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODU3MzgyMjR8MA&ixlib=rb-4.1.0&q=85"
            alt="Premium home" className="w-full h-[380px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-900/30" />
          <div className="absolute inset-0 flex flex-col justify-center px-8 lg:px-16 max-w-2xl">
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-extrabold text-white">
              Your Dream Home Deserves a Second Opinion.
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed">
              Before you commit your money, understand your cost, materials, quotations and construction risks.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button onClick={createProject} data-testid="cta-primary"
                className="bg-[#0E9AC8] hover:bg-white hover:text-slate-900 text-white rounded-xl h-14 px-8 text-base font-semibold transition-colors">
                Create My Project
              </Button>
              <Button onClick={askNcis} data-testid="cta-secondary" variant="outline"
                className="rounded-xl h-14 px-7 bg-transparent text-white border-white/40 hover:bg-white hover:text-slate-900 transition-colors">
                <MessageSquare className="mr-2 h-4 w-4" strokeWidth={2} /> Ask NCIS™
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
